import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile wrapper.
 *
 * STUB: uses the official Cloudflare test site key (`1x00000000000000000000AA`)
 * which always passes. Open Question #3 in the product brief needs an answer
 * before production deploy — swap `SITE_KEY` for Upside's real key.
 */

const SITE_KEY = "1x00000000000000000000AA";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: { sitekey: string; callback: (token: string) => void; "error-callback"?: () => void },
      ) => string;
      reset: (widgetId?: string) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

type Props = {
  onToken: (token: string | null) => void;
};

export function Turnstile({ onToken }: Props) {
  const container = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    const id = "cf-turnstile-script";
    if (!document.getElementById(id)) {
      const script = document.createElement("script");
      script.id = id;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const render = () => {
      if (!container.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(container.current, {
        sitekey: SITE_KEY,
        callback: (token) => onToken(token),
        "error-callback": () => onToken(null),
      });
    };

    if (window.turnstile) {
      render();
    } else {
      window.onloadTurnstileCallback = render;
    }

    return () => {
      if (window.turnstile && widgetId.current) {
        try {
          window.turnstile.reset(widgetId.current);
        } catch {
          // no-op
        }
      }
    };
  }, [onToken]);

  return <div ref={container} aria-label="Anti-bot check" style={{ marginTop: 12 }} />;
}
